import styled from 'styled-components'
import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Dropdown, { components } from 'react-select'
import { FormattedMessage, useIntl } from 'react-intl'
import Tooltip from 'components/Tooltip'
import { TrailingIconsContainer } from 'components/Input/style'
import ErrorIcon from 'assets/icons/error-input-trailing-icon'
import InfoIcon from 'assets/icons/info-icon16px'
import SearchIcon from 'assets/icons/search-icon'
import { SearchSelectIconContainer, SearchSelectIconWrapper, Label, Error, SelectContainer } from './styles'
import {
  createDropdownIndicatorClosed,
  createPlaceholder,
  SingleValueDefault,
  MenuListDefault,
  buildSelectStyles,
  MultiSelectOptionWithRemove,
  tooltipXOffset,
  tooltipYOffset
} from './shared'

const SelectTrailingIconsContainer = styled(TrailingIconsContainer)`
  right: 32px;

  img {
    margin-right: 0;
  }
`

/** Marker for clicks that must not run react-select Menu onMouseDown (focusInput steals focus). */
const MENU_FILTER_SEARCH = 'data-rs-menu-filter-search'

/**
 * Wraps default Menu so mousedowns on the in-menu filter field do not call the library handler
 * that refocuses the combobox and closes the menu before the filter input can take focus.
 */
function MenuWithSearchFilterGuard (props) {
  const { children, innerProps, ...rest } = props
  const upstreamOnMouseDown = innerProps?.onMouseDown
  return (
    <components.Menu
      {...rest}
      innerProps={{
        ...innerProps,
        onMouseDown: (event) => {
          if (event.button !== 0) return
          const { target } = event
          if (typeof target?.closest === 'function' && target.closest(`[${MENU_FILTER_SEARCH}]`)) {
            event.stopPropagation()
            return
          }
          if (upstreamOnMouseDown) upstreamOnMouseDown(event)
        }
      }}
    >
      {children}
    </components.Menu>
  )
}

MenuWithSearchFilterGuard.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({
    onMouseDown: PropTypes.func
  })
}

/**
 * Must stay a module-level component (stable identity). If defined inside Select, each parent
 * re-render creates a new function type and React remounts the menu list — the filter input
 * loses focus after every keystroke.
 */
function MenuListWithInMenuSearch (props) {
  const root = props.selectProps || {}
  const extra = root.selectProps || {}
  const searchInMenu = root.searchInMenu
  const isMulti = root.isMulti
  const {
    inMenuSearchText,
    onInMenuSearchChange,
    inMenuSearchWrapperRef,
    inMenuSearchPlaceholder,
    inMenuSearchAriaLabel,
    baseMenuList: BaseMenuListProp,
  } = extra

  const BaseMenuList = BaseMenuListProp || MenuListDefault

  if (!searchInMenu || !isMulti) {
    return <BaseMenuList {...props} />
  }

  return (
    <components.MenuList {...props} className={[props.className, 'scroll'].filter(Boolean).join(' ')}>
      <div
        role="none"
        ref={inMenuSearchWrapperRef}
        {...{ [MENU_FILTER_SEARCH]: '' }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid rgba(32, 32, 32, 0.12)',
          background: '#fff',
        }}
      >
        <input
          type="text"
          autoComplete="off"
          value={inMenuSearchText ?? ''}
          onChange={(e) => onInMenuSearchChange?.(e.target.value)}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            const el = e.currentTarget
            queueMicrotask(() => {
              el.focus()
            })
          }}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder={inMenuSearchPlaceholder}
          aria-label={inMenuSearchAriaLabel || inMenuSearchPlaceholder}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: '36px',
            border: '1px solid rgba(32, 32, 32, 0.2)',
            borderRadius: '4px',
            padding: '0 10px',
            fontSize: '14px',
          }}
        />
      </div>
      {props.children}
    </components.MenuList>
  )
}

MenuListWithInMenuSearch.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  selectProps: PropTypes.object
}

const InputWithTitle = (props) => {
  const inputTitle = props.selectProps?.title
  return <components.Input {...props} title={inputTitle} aria-label={inputTitle} />
}
InputWithTitle.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    title: PropTypes.string
  })
}

function renderFormattedLabelBlock ({
  label,
  field,
  dataTest,
  marginBottomLabel,
  infoTooltip,
  zIndex,
  link,
  hyper,
  isOptional,
  showSearchGlyph
}) {
  return (
    <>
      <Label htmlFor={`dropdown-${field}`} data-test={`label-${dataTest}`} marginBottomLabel={marginBottomLabel}>
        <section style={{ display: 'flex', alignItems: 'center' }}>
          <FormattedMessage id={label} />
          {infoTooltip && (
            <Tooltip
              style={{ zIndex: 1000 }}
              zIndex={zIndex}
              placement='top'
              trigger={['hover']}
              align={{ offset: [tooltipXOffset, tooltipYOffset] }}
              overlay={{ label: label, infoTooltip: infoTooltip }}
              link={link}
              hyper={hyper}
            >
              <div>
                <InfoIcon />
              </div>
            </Tooltip>
          )}
        </section>
        <span>{isOptional && <FormattedMessage id='optional' />}</span>
      </Label>
      {showSearchGlyph && (
        <SearchSelectIconContainer>
          <SearchSelectIconWrapper>
            <SearchIcon />
          </SearchSelectIconWrapper>
        </SearchSelectIconContainer>
      )}
    </>
  )
}

renderFormattedLabelBlock.propTypes = {
  label: PropTypes.string,
  field: PropTypes.string,
  dataTest: PropTypes.string,
  marginBottomLabel: PropTypes.string,
  infoTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  link: PropTypes.string,
  hyper: PropTypes.string,
  isOptional: PropTypes.bool,
  showSearchGlyph: PropTypes.bool
}

function renderValidationError (error, field, errorMarginTop) {
  if (!error || error.field !== field) {
    return null
  }
  return (
    <Error errorMarginTop={errorMarginTop} data-test='validation-error-message'>
      <FormattedMessage id={error.error} />
    </Error>
  )
}

function menuListOptionMatchesFilter (option, _inputValue, menuListSearch, value) {
  const q = menuListSearch.trim().toLowerCase()
  if (!q) return true
  const data = option.data ?? option
  const optVal = data?.value ?? option.value
  const vals = Array.isArray(value) ? value : []
  if (vals.some((v) => (v?.value ?? v) === optVal)) return true
  const lab = data?.label ?? option.label
  if (typeof lab === 'string') return lab.toLowerCase().includes(q)
  return String(optVal ?? '').toLowerCase().includes(q)
}

function scheduleInMenuSearchClose (menuFilterRef, setMenuListSearch, setSearchMenuOpen) {
  // react-select may call onMenuClose from onInputBlur before focus has moved to the
  // in-menu filter input; defer so document.activeElement reflects the filter field.
  const finish = () => {
    const active = document.activeElement
    if (menuFilterRef.current && active && menuFilterRef.current.contains(active)) {
      return
    }
    setMenuListSearch('')
    setSearchMenuOpen(false)
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(finish)
  })
}

function buildSelectComponentRegistry ({
  searchInMenu,
  isMulti,
  multiMenuRemove,
  indicatorSize,
  PlaceholderComponent,
  placeholder,
  SingleValueComponent,
}) {
  const DropdownIndicator = createDropdownIndicatorClosed(indicatorSize)
  const Placeholder = PlaceholderComponent || createPlaceholder(placeholder)
  const SingleValue = SingleValueComponent || SingleValueDefault
  return {
    ...(searchInMenu && isMulti ? { Menu: MenuWithSearchFilterGuard } : {}),
    DropdownIndicator,
    Placeholder,
    SingleValue,
    MenuList: MenuListWithInMenuSearch,
    Input: InputWithTitle,
    ...(multiMenuRemove && isMulti ? { Option: MultiSelectOptionWithRemove } : {}),
  }
}

function renderTrailingFieldErrorIcon (error, field) {
  if (!error || error.field !== field) {
    return null
  }
  return <ErrorIcon />
}

function resolveFilterOptionForSelect (searchInMenu, isMulti, menuListFilterOption, filterOption) {
  if (searchInMenu && isMulti) {
    return menuListFilterOption
  }
  return filterOption
}

function computeControlMenuForInMenuSearch (menuIsOpen, searchInMenu, isMulti) {
  return Boolean(searchInMenu && isMulti && menuIsOpen === undefined)
}

function resolveSelectPaddingLeft (searchInMenu, isMulti, paddingLeft) {
  return searchInMenu && isMulti ? '8px' : paddingLeft
}

function renderSelectLabelSection ({
  label,
  field,
  dataTest,
  marginBottomLabel,
  infoTooltip,
  zIndex,
  link,
  hyper,
  isOptional,
  showSelectSearchGlyph,
}) {
  if (React.isValidElement(label)) {
    return label
  }
  return (
    <>
      {label &&
        renderFormattedLabelBlock({
          label,
          field,
          dataTest,
          marginBottomLabel,
          infoTooltip,
          zIndex,
          link,
          hyper,
          isOptional,
          showSearchGlyph: showSelectSearchGlyph,
        })}
      {showSelectSearchGlyph && !label && (
        <SearchSelectIconContainer>
          <SearchSelectIconWrapper>
            <SearchIcon />
          </SearchSelectIconWrapper>
        </SearchSelectIconContainer>
      )}
    </>
  )
}

renderSelectLabelSection.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  field: PropTypes.string,
  dataTest: PropTypes.string,
  marginBottomLabel: PropTypes.string,
  infoTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  link: PropTypes.string,
  hyper: PropTypes.string,
  isOptional: PropTypes.bool,
  showSelectSearchGlyph: PropTypes.bool,
}

const Select = ({
  value,
  onChange,
  options,
  label,
  field,
  error,
  errorMarginTop = "16px",
  placeholder,
  height = '48px',
  menuPlacement,
  marginTop = '8px',
  marginBottom = '32px',
  indicatorSize,
  dataTest,
  disabled,
  placeholderComponent: PlaceholderComponent,
  closeMenuOnSelect = true,
  singleValueComponent: SingleValueComponent,
  menuListComponent: MenuListComponent,
  infoTooltip,
  zIndex,
  isSearchable = false,
  paddingLeft = `${isSearchable ? '30px' : '8px'}`,
  menuWidth = '100%',
  menuIsOpen,
  onFocus,
  onBlur,
  inputValue,
  onInputChange,
  isOptional = false,
  link,
  twoStepWidth,
  hyper,
  searchDropdown = false,
  marginBottomLabel,
  inputMargin = '12px 0 12px 8px',
  filterOption,
  title,
  visuallyDisabled = false,
  isMulti = false,
  isClearable = false,
  multiMenuRemove = false,
  searchInMenu = false,
}) => {
  const intl = useIntl()
  const menuFilterRef = useRef(null)
  const controlMenuForInMenuSearch = computeControlMenuForInMenuSearch(menuIsOpen, searchInMenu, isMulti)
  const [searchMenuOpen, setSearchMenuOpen] = useState(false)
  const [menuListSearch, setMenuListSearch] = useState('')
  const effectiveSearchable = Boolean(isSearchable) && !(searchInMenu && isMulti)
  const resolvedPaddingLeft = resolveSelectPaddingLeft(searchInMenu, isMulti, paddingLeft)

  const menuListFilterOption = useCallback(
    (option, inputValue) => menuListOptionMatchesFilter(option, inputValue, menuListSearch, value),
    [menuListSearch, value]
  )

  const resolvedFilterOption = resolveFilterOptionForSelect(
    searchInMenu,
    isMulti,
    menuListFilterOption,
    filterOption
  )

  const customStyles = buildSelectStyles({
    error,
    field,
    height,
    menuWidth,
    inputMargin,
    paddingLeft: resolvedPaddingLeft,
    searchDropdown,
    twoStepWidth,
    visuallyDisabled,
    allowDisabledPointerStyle: true,
    includeExpandedMenu: false,
    isMulti,
    zIndex,
  })

  const filterPlaceholder = intl.formatMessage({
    id: 'siteFilterMenuSearchPlaceholder',
    defaultMessage: 'Type to filter…'
  })

  const selectComponents = buildSelectComponentRegistry({
    searchInMenu,
    isMulti,
    multiMenuRemove,
    indicatorSize,
    PlaceholderComponent,
    placeholder,
    SingleValueComponent,
  })

  const onBlurHandler = useCallback(() => {
    onBlur?.()
  }, [onBlur])

  const showSelectSearchGlyph = effectiveSearchable && !searchDropdown

  const handleMenuOpen = useCallback(() => {
    if (controlMenuForInMenuSearch) {
      setSearchMenuOpen(true)
    }
  }, [controlMenuForInMenuSearch])

  const handleMenuClose = useCallback(() => {
    if (!controlMenuForInMenuSearch) {
      setMenuListSearch('')
      return
    }
    scheduleInMenuSearchClose(menuFilterRef, setMenuListSearch, setSearchMenuOpen)
  }, [controlMenuForInMenuSearch])

  return (
    <SelectContainer
      data-testid='test-select-container'
      marginBottom={marginBottom}
      onClick={disabled ? undefined : () => {}}
    >
      {renderSelectLabelSection({
        label,
        field,
        dataTest,
        marginBottomLabel,
        infoTooltip,
        zIndex,
        link,
        hyper,
        isOptional,
        showSelectSearchGlyph,
      })}
      <div style={{ position: 'relative' }}>
        <Dropdown
          components={selectComponents}
          id={`dropdown-${field}`}
          value={value}
          onChange={onChange}
          styles={customStyles}
          options={options}
          menuPlacement={menuPlacement || 'auto'}
          menuPortalTarget={document.body}
          data-test={dataTest}
          searchInMenu={searchInMenu}
          selectProps={{
            inMenuSearchText: menuListSearch,
            onInMenuSearchChange: setMenuListSearch,
            inMenuSearchWrapperRef: menuFilterRef,
            inMenuSearchPlaceholder: filterPlaceholder,
            inMenuSearchAriaLabel: filterPlaceholder,
            baseMenuList: MenuListComponent || MenuListDefault
          }}
          isSearchable={effectiveSearchable}
          isDisabled={disabled}
          isMulti={isMulti}
          isClearable={isClearable}
          hideSelectedOptions={isMulti ? false : undefined}
          closeMenuOnSelect={isMulti ? false : closeMenuOnSelect}
          menuIsOpen={controlMenuForInMenuSearch ? searchMenuOpen : menuIsOpen}
          onFocus={onFocus}
          onBlur={onBlurHandler}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          inputValue={inputValue}
          onInputChange={onInputChange}
          filterOption={resolvedFilterOption}
          title={title}
        />
        <SelectTrailingIconsContainer>
          {renderTrailingFieldErrorIcon(error, field)}
        </SelectTrailingIconsContainer>
      </div>
      {renderValidationError(error, field, errorMarginTop)}
    </SelectContainer>
  )
}

export default Select

Select.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.any),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  field: PropTypes.string,
  error: PropTypes.shape({
    field: PropTypes.string,
    error: PropTypes.string
  }),
  errorMarginTop: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.string,
  menuPlacement: PropTypes.string,
  marginTop: PropTypes.string,
  marginBottom: PropTypes.string,
  indicatorSize: PropTypes.string,
  dataTest: PropTypes.string,
  disabled: PropTypes.bool,
  placeholderComponent: PropTypes.elementType,
  closeMenuOnSelect: PropTypes.bool,
  singleValueComponent: PropTypes.elementType,
  menuListComponent: PropTypes.elementType,
  infoTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSearchable: PropTypes.bool,
  paddingLeft: PropTypes.string,
  menuWidth: PropTypes.string,
  menuIsOpen: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  isOptional: PropTypes.bool,
  link: PropTypes.string,
  twoStepWidth: PropTypes.bool,
  hyper: PropTypes.string,
  searchDropdown: PropTypes.bool,
  marginBottomLabel: PropTypes.string,
  inputMargin: PropTypes.string,
  filterOption: PropTypes.func,
  title: PropTypes.string,
  visuallyDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  multiMenuRemove: PropTypes.bool,
  searchInMenu: PropTypes.bool
}